import React, { useState, useEffect } from 'react';
import appScriptService from '../utils/appScriptService';
import aiSummaryService from '../utils/aiSummaryService';
import gcsService from '../utils/gcsService';

const Interview = () => {
  const [selectedModule, setSelectedModule] = useState('BE001_SPRING');
  const [learningData, setLearningData] = useState([]);
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
  const [interviewMode, setInterviewMode] = useState('preparation'); // preparation, simulation, review
  const [isAIAvailable, setIsAIAvailable] = useState(true); // AI 연결 상태

  // 질문 개수 선택 관련 상태
  const [questionCount, setQuestionCount] = useState(5);
  const [maxQuestions, setMaxQuestions] = useState(0);

  // 타이머 관련 상태
  const [answerTimeLeft, setAnswerTimeLeft] = useState(null); // 답변 제한시간 (초)
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // NCS 모듈 목록
  const modules = [
    { id: 'LM2001020230_프로그래밍언어응용', name: '프로그래밍 언어 응용' },
    { id: 'LM2001020231_프로그래밍언어활용', name: '프로그래밍 언어 활용' },
    { id: 'LM2001020224_화면설계', name: '화면설계' },
    { id: 'LM2001020225_화면구현', name: '화면구현' },
    { id: 'LM2001020211_서버프로그램구현', name: '서버프로그램구현' },
    { id: 'LM2001020227_애플리케이션테스트수행', name: '애플리케이션테스트수행' },
    { id: 'LM2001020205_데이터입출력구현', name: '데이터입출력구현' },
    { id: 'LM2001020206_통합구현', name: '통합구현' },
    { id: 'LM2001020214_애플리케이션배포', name: '애플리케이션배포' },
    { id: 'LM2001020212_인터페이스구현', name: '인터페이스구현' },
    // 추가 카테고리
    { id: 'BE001_SPRING', name: 'Spring 백엔드' },
    { id: 'FE001_REACT', name: 'React 프론트엔드' },
    { id: 'HR001_COMMON', name: '공통 면접질문' },
  ];

  // 질문 개수 옵션
  const questionOptions = [
    { value: 3, label: '3개 질문', time: '약 10분', desc: '빠른 연습' },
    { value: 5, label: '5개 질문', time: '약 15분', desc: '기본 면접' },
    { value: 10, label: '10개 질문', time: '약 30분', desc: '심화 면접' },
    { value: 0, label: '전체 질문', time: '', desc: '모든 문제' }
  ];

  useEffect(() => {
    loadLearningData();
  }, [selectedModule]);

  // 타이머 효과
  useEffect(() => {
    let timer;
    if (isTimerActive && answerTimeLeft > 0) {
      timer = setInterval(() => {
        setAnswerTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (answerTimeLeft === 0) {
      setIsTimerActive(false);
      // 시간 초과 시 자동으로 피드백 생성 (시간 초과 버전)
      const currentQ = interviewQuestions[currentQuestion];
      alert('답변시간이 지났습니다.');
      setFeedback({
        score: null,
        strengths: [],
        improvements: ['시간 내에 답변을 완료하지 못했습니다. 면접에서는 시간 관리가 중요합니다.'],
        detailedFeedback: '제한 시간이 초과되었습니다. 아래 모범답안을 참고하여 다음번에는 더 간결하고 체계적으로 답변해보세요.',
        keywordMatch: null,
        question: currentQ.question,
        userAnswer: userAnswer || '(시간 초과로 답변 미완료)',
        isOfflineMode: true,
        isTimeOut: true
      });
    }
    return () => clearInterval(timer);
  }, [isTimerActive, answerTimeLeft]);

  // isGenerating 상태 변화 모니터링
  useEffect(() => {

  }, [isGenerating]);

  // 학습 데이터 로드
  const loadLearningData = async () => {
    try {
      // 과목코드 필터링된 데이터만 가져오기
      const data = await appScriptService.getData('AI요약', {
        과목코드: selectedModule
      });

      const summariesData = Array.isArray(data) ? data : (data?.data || []);
      setLearningData(summariesData);

    } catch (error) {
      console.error('학습 데이터 로드 실패:', error);
    }
  };

  // 저장된 면접 질문 불러오기 (폴백)
  const loadSavedQuestions = async () => {
    try {

      const savedQuestions = await appScriptService.getData('면접질문', {
        과목코드: selectedModule
      });
      
      if (savedQuestions && savedQuestions.length > 0) {
        // maxQuestions 업데이트
        setMaxQuestions(savedQuestions.length);

        // GCS 서비스를 사용해 영상 URL을 Signed URL로 변환
        const limit = questionCount === 0 ? savedQuestions.length : questionCount;
        const processedQuestions = await gcsService.processInterviewQuestions(savedQuestions.slice(0, limit));

        const formattedQuestions = processedQuestions.map(q => ({
          question: q.질문,
          videoUrl: q.영상 || null, // GCS에서 Signed URL로 변환된 값
          expectedKeywords: typeof q.예상키워드 === 'string' ? q.예상키워드.split(', ') : (q.예상키워드 || []),
          modelAnswer: q.모범답안,
          difficulty: q.난이도 || '중',
          relatedTopic: q.토픽명,
          relatedData: null
        }));

        return formattedQuestions;
      }
      
      return null;
    } catch (error) {
      console.error('저장된 질문 불러오기 실패:', error);
      return null;
    }
  };

  // AI 면접 질문 생성
  const generateInterviewQuestions = async () => {
    setIsGenerating(true);
    
    // 상태 업데이트를 확실히 하기 위해 약간의 지연
    await new Promise(resolve => setTimeout(resolve, 10));

    
    try {
      // 먼저 저장된 질문이 있는지 확인
      const savedQuestions = await loadSavedQuestions();
      if (savedQuestions && savedQuestions.length > 0) {
        setInterviewQuestions(savedQuestions);
        setCurrentQuestion(0);
        setInterviewMode('simulation');
        setIsAIAvailable(true); // 저장된 질문 사용 시에도 AI 피드백 활성화
        setIsGenerating(false);
        return;
      }
      

    
    } catch (fallbackError) {

    }
    
    try {
      // 학습 데이터가 없으면 오류 메시지 표시
      if (learningData.length === 0) {
        alert('해당 모듈의 학습 데이터가 없습니다. 다른 모듈을 선택해주세요.');
        setIsGenerating(false);
        return;
      }

      const questions = [];

      const limit = questionCount === 0 ? learningData.length : questionCount;
      for (const item of learningData.slice(0, limit)) { // 선택한 개수만큼 제한
        const prompt = `
다음 NCS 학습 내용을 바탕으로 실제 면접에서 나올 수 있는 질문 1개를 생성해주세요.

**학습 정보:**
- 토픽: ${item.토픽명}
- 핵심개념: ${item.핵심개념}
- 실무활용: ${item.실무활용}
- 키워드: ${item.키워드}

**요구사항:**
1. 당신은 수년간의 개발자 채용을 전담으로 한 개발기업의 개발자이자 면접담당자입니다.
2. 해당 토픽의 실무 이해도를 측정할 수 있는 질문이어야 합니다.
3. 구체적이고 명확한 질문이어야 합니다.
4. 50자 이내의 간결한 질문이어야 합니다.

JSON 형태로 응답해주세요:
{
  "question": "면접 질문",
  "expectedKeywords": ["기대되는", "키워드", "들"],
  "modelAnswer": "모범 답안 예시",
  "difficulty": "상/중/하"
}`;

        const response = await aiSummaryService.generateSummary(prompt);
        
        try {
          // JSON 추출 (마크다운 코드 블록 제거)
          const jsonMatch = response.match(/```json\n([\s\S]*?)\n```/) || response.match(/\{[\s\S]*\}/);
          const jsonStr = jsonMatch ? (jsonMatch[1] || jsonMatch[0]) : response;
          
          const questionData = JSON.parse(jsonStr);
          questions.push({
            ...questionData,
            videoUrl: null, // AI 생성 질문에는 영상 없음
            relatedTopic: item.토픽명,
            relatedData: item
          });
        } catch (parseError) {
          console.error('JSON 파싱 실패:', parseError);
          // 파싱 실패 시 기본 질문 생성
          questions.push({
            question: `${item.토픽명}에 대해 실무에서 어떻게 활용하실 건가요?`,
            videoUrl: null, // AI 생성 질문에는 영상 없음
            expectedKeywords: item.키워드?.split(',') || [],
            modelAnswer: item.실무활용 || '구체적인 실무 경험을 바탕으로 답변하세요.',
            difficulty: item.중요도 || '중',
            relatedTopic: item.토픽명,
            relatedData: item
          });
        }
      }

      setInterviewQuestions(questions);
      setCurrentQuestion(0);
      setInterviewMode('simulation');
      setIsAIAvailable(true); // AI 생성 성공 시 AI 활성화 모드

      
      // 생성된 질문들을 구글 시트에 저장
      await saveQuestionsToSheet(questions);
      
    } catch (error) {
      console.error('AI 면접 질문 생성 실패:', error);
      
      // AI 실패 시 저장된 질문으로 폴백 시도
      try {

        const savedQuestions = await loadSavedQuestions();
        if (savedQuestions && savedQuestions.length > 0) {
          setInterviewQuestions(savedQuestions);
          setCurrentQuestion(0);
          setInterviewMode('simulation');
          setIsAIAvailable(true); // 폴백 사용 시에도 AI 피드백 활성화
          return;
        }
      } catch (fallbackError) {
        console.error('폴백도 실패:', fallbackError);
      }
      
      alert('면접 질문 생성에 실패했습니다. 네트워크 연결을 확인하고 다시 시도해주세요.');
    } finally {

      setIsGenerating(false);
    }
  };

  // 답변 제출 및 피드백 생성
  const submitAnswer = async () => {
    if (!userAnswer.trim()) {
      alert('답변을 입력해주세요.');
      return;
    }

    // 답변 제출 시 타이머 중지
    stopAnswerTimer();

    setIsSubmittingAnswer(true);
    const currentQ = interviewQuestions[currentQuestion];
    
    // AI 비활성화 모드일 때는 간소화된 피드백만 제공
    if (!isAIAvailable) {
      setFeedback({
        score: null,
        strengths: ['답변을 작성해주셨습니다.'],
        improvements: ['모범답안을 참고해서 더 구체적으로 답변해보세요.'],
        detailedFeedback: 'AI 연결이 없어 상세 피드백을 제공할 수 없습니다. 모범답안과 예상키워드를 참고해주세요.',
        keywordMatch: null,
        question: currentQ.question,
        userAnswer: userAnswer,
        isOfflineMode: true
      });
      setIsSubmittingAnswer(false);
      return;
    }
    
    console.log('=== AI 피드백 생성 ===');
    console.log('질문:', currentQ.question);
    console.log('사용자 답변:', userAnswer);
    console.log('예상 키워드:', currentQ.expectedKeywords);

    const feedbackPrompt = `
You are a very strict senior interviewer at a top IT company. Evaluate this interview answer extremely harshly and provide feedback in Korean.

**Question:** ${currentQ.question}
**Expected Keywords:** ${currentQ.expectedKeywords.join(', ')}
**Candidate's Answer:** ${userAnswer}
**Model Answer:** ${currentQ.modelAnswer}

**CRITICAL SCORING RULES (Apply strictly):**
- Single characters like "1", "2" → 0-10 points
- Answers under 10 characters → 0-15 points
- No expected keywords → 0-25 points
- No technical explanation → maximum 40 points
- Irrelevant answers → 0-20 points

**Evaluation Criteria:**
1. Technical accuracy and depth (requires detailed explanation)
2. Inclusion of expected keywords (must match closely)
3. Practical experience and specific examples
4. Logical structure and clarity
5. Answer completeness (minimum 50+ characters expected)

**Scoring Scale (Be extremely strict):**
- 90-100: Perfect expert-level answer (all keywords, deep technical explanation, real examples)
- 80-89: Excellent (most keywords, good technical accuracy)
- 70-79: Average (some keywords, basic explanation)
- 50-69: Poor (missing keywords, insufficient explanation)
- 0-49: Very poor (meaningless, irrelevant, or too short answers)

**IMPORTANT: Be harsh with scoring. Most answers should get low scores unless they are truly comprehensive and professional.

Respond in JSON format with Korean feedback:
{
  "score": numeric_score(0-100),
  "strengths": ["specific_strengths_in_Korean"],
  "improvements": ["specific_improvements_in_Korean"],
  "detailedFeedback": "detailed_feedback_in_Korean",
  "keywordMatch": keyword_match_percentage(0-100)
}`;

    try {
      const feedbackResponse = await aiSummaryService.generateSummary(feedbackPrompt);
      
      // JSON 추출 (마크다운 코드 블록 제거)
      const jsonMatch = feedbackResponse.match(/```json\n([\s\S]*?)\n```/) || feedbackResponse.match(/\{[\s\S]*\}/);
      const jsonStr = jsonMatch ? (jsonMatch[1] || jsonMatch[0]) : feedbackResponse;
      
      const feedbackData = JSON.parse(jsonStr);
      
      setFeedback({
        ...feedbackData,
        question: currentQ.question,
        userAnswer: userAnswer
      });
      
    } catch (error) {
      console.error('피드백 생성 실패:', error);
      setFeedback({
        score: 70,
        strengths: ['답변을 작성해주셨습니다.'],
        improvements: ['더 구체적인 사례를 들어보세요.'],
        detailedFeedback: '피드백 생성 중 오류가 발생했습니다. 다시 시도해주세요.',
        keywordMatch: 50,
        question: currentQ.question,
        userAnswer: userAnswer
      });
    } finally {
      setIsSubmittingAnswer(false);
    }
  };

  // 생성된 질문들을 구글 시트에 저장
  const saveQuestionsToSheet = async (questions) => {
    try {

      
      const questionsData = questions.map(q => ({
        과목코드: selectedModule,
        토픽명: q.relatedTopic || '',
        질문: q.question || '',
        예상키워드: q.expectedKeywords || [],
        모범답안: q.modelAnswer || '',
        난이도: q.difficulty || '중',
        생성일시: new Date().toISOString(),
        사용횟수: 0
      }));
      
      await appScriptService.addData('면접질문', questionsData);

      
    } catch (error) {
      console.error('면접 질문 시트 저장 실패:', error);
      // 저장 실패해도 면접 진행은 계속
    }
  };

  // 답변 타이머 시작
  const startAnswerTimer = () => {
    setVideoEnded(true);
    setIsVideoPlaying(false);
    setAnswerTimeLeft(180); // 3분 (180초)
    setIsTimerActive(true);
  };

  // 타이머 중지
  const stopAnswerTimer = () => {
    setIsTimerActive(false);
    setAnswerTimeLeft(null);
  };

  // 영상 재생/일시정지 토글
  const toggleVideo = () => {
    const video = document.querySelector('video');
    if (video) {
      if (video.paused) {
        video.play();
        setIsVideoPlaying(true);
      } else {
        video.pause();
        setIsVideoPlaying(false);
      }
    }
  };

  // 다음 질문으로
  const nextQuestion = () => {
    setUserAnswer('');
    setFeedback(null);
    stopAnswerTimer();
    setVideoEnded(false);
    setIsVideoPlaying(false);

    if (currentQuestion < interviewQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setInterviewMode('review');
    }
  };

  // 시간 포맷팅 함수
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSelectedModuleInfo = () => {
    return modules.find(module => module.id === selectedModule) || modules[0];
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* 헤더 */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            🎤 AI 면접 준비
          </h1>
          <p className="text-gray-600 text-lg">
            실전 같은 면접 연습으로 완벽하게 준비하세요
          </p>
        </div>

        {/* 준비 모드 */}
        {interviewMode === 'preparation' && (
          <div className="space-y-6">
            {/* 모듈 선택 */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-semibold mb-4 flex items-center">
                📚 학습 모듈 선택
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    면접 준비할 모듈
                  </label>
                  <select
                    value={selectedModule}
                    onChange={(e) => setSelectedModule(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {modules.map(module => (
                      <option key={module.id} value={module.id}>
                        {module.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🎯 면접 질문 개수
                  </label>
                  <select
                    value={questionCount}
                    onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {questionOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label} {option.time && `(${option.time})`}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {questionOptions.find(opt => opt.value === questionCount)?.desc}
                    {questionCount === 0 && maxQuestions > 0 && ` (총 ${maxQuestions}개)`}
                  </p>
                </div>
              </div>
            </div>

            {/* 면접 시작 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🚀</span>
                </div>
                <h3 className="text-2xl font-semibold mb-2">면접 준비 완료!</h3>
                <p className="text-gray-600">
                  {getSelectedModuleInfo().name} 모듈에서<br/>
                  {questionCount === 0 ? '모든' : questionCount}개의 면접 질문을 준비합니다.
                  {questionOptions.find(opt => opt.value === questionCount)?.time &&
                    ` (${questionOptions.find(opt => opt.value === questionCount)?.time})`}
                </p>
              </div>
              
              <div className="flex justify-center">
                <button
                  onClick={generateInterviewQuestions}
                  disabled={isGenerating}
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full text-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center"
                >
                  {(() => {

                    return isGenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        AI 면접 질문 생성 중...
                      </>
                    ) : (
                      '🎯 면접 시작하기'
                    );
                  })()}
                </button>
              </div>
              
              {learningData.length === 0 && (
                <p className="text-red-500 text-sm mt-4">
                  먼저 해당 모듈의 학습 데이터가 필요합니다.
                </p>
              )}
            </div>
          </div>
        )}

        {/* 면접 시뮬레이션 모드 */}
        {interviewMode === 'simulation' && interviewQuestions.length > 0 && (
          <div className="space-y-6">
            {/* 진행 상황 */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">
                  질문 {currentQuestion + 1} / {interviewQuestions.length}
                </h2>
                <div className="text-sm text-gray-500">
                  {interviewQuestions[currentQuestion].relatedTopic}
                </div>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentQuestion + 1) / interviewQuestions.length) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* 현재 질문 */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${
                    isAIAvailable 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                      : 'bg-gradient-to-r from-gray-500 to-gray-600'
                  }`}>
                    <span className="text-white font-bold">Q</span>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">
                      {isAIAvailable ? '면접관 질문' : '면접관 질문 (오프라인 모드)'}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`text-sm px-2 py-1 rounded text-white inline-block ${
                        interviewQuestions[currentQuestion].difficulty === '상' ? 'bg-red-500' :
                        interviewQuestions[currentQuestion].difficulty === '중' ? 'bg-yellow-500' : 'bg-green-500'
                      }`}>
                        난이도: {interviewQuestions[currentQuestion].difficulty}
                      </div>
                      {!isAIAvailable && (
                        <div className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded">
                          AI 피드백 비활성화
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* 영상이 있으면 비디오 표시, 없으면 텍스트 질문 표시 */}
                {interviewQuestions[currentQuestion].videoUrl ? (
                  <div className="space-y-4">
                    <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                      <video
                        className="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
                        preload="metadata"
                        style={{ maxHeight: '400px' }}
                        onEnded={startAnswerTimer}
                        onPlay={() => setIsVideoPlaying(true)}
                        onPause={() => setIsVideoPlaying(false)}
                      >
                        <source src={interviewQuestions[currentQuestion].videoUrl} type="video/mp4" />
                        <p className="text-center p-4 text-gray-600">
                          비디오를 재생할 수 없습니다. 브라우저가 비디오 형식을 지원하지 않습니다.
                        </p>
                      </video>

                      {/* 사용자 정의 컨트롤 */}
                      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between bg-black bg-opacity-50 rounded-lg p-2">
                        <button
                          onClick={toggleVideo}
                          className="text-white p-2 hover:bg-white hover:bg-opacity-20 rounded"
                        >
                          {isVideoPlaying ? '⏸️' : '▶️'}
                        </button>

                        {videoEnded && (
                          <div className="text-white text-sm">
                            질문이 끝났습니다. 답변을 시작하세요!
                          </div>
                        )}

                        <div className="text-white text-sm">
                          🔊
                        </div>
                      </div>
                    </div>

                    {/* 영상 아래에 텍스트 질문도 표시 (백업용) */}
                    <div className="text-lg font-medium text-gray-700 text-center bg-gray-50 p-4 rounded-lg">
                      {interviewQuestions[currentQuestion].question}
                    </div>
                  </div>
                ) : (
                  <div className="text-xl font-medium text-gray-800 leading-relaxed">
                    {interviewQuestions[currentQuestion].question}
                  </div>
                )}

              </div>

              {/* 타이머 표시 */}
              {isTimerActive && answerTimeLeft !== null && (
                <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white p-4 rounded-lg mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-2xl mr-2">⏰</span>
                      <span className="font-semibold">답변 시간</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {formatTime(answerTimeLeft)}
                    </div>
                  </div>
                  <div className="w-full bg-white bg-opacity-30 rounded-full h-2 mt-2">
                    <div
                      className="bg-white h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${(answerTimeLeft / 180) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* 답변 입력 */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    🎯 귀하의 답변을 입력해주세요
                  </label>
                  {!isTimerActive && interviewQuestions[currentQuestion].videoUrl && !videoEnded && (
                    <div className="text-sm text-gray-500">
                      영상을 시청한 후 답변을 시작하세요
                    </div>
                  )}
                  {!interviewQuestions[currentQuestion].videoUrl && (
                    <div className="text-sm text-gray-500">
                      자유롭게 답변을 작성해주세요
                    </div>
                  )}
                </div>
                <textarea
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  placeholder={isTimerActive ? "시간 제한이 있습니다. 신속하고 명확하게 답변해주세요..." : "구체적인 경험과 사례를 바탕으로 답변해주세요..."}
                  className={`w-full h-32 px-4 py-3 border rounded-lg focus:ring-2 focus:border-purple-500 resize-none ${
                    isTimerActive ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-purple-500'
                  }`}
                />
                
                <div className="flex gap-2">
                  <button
                    onClick={submitAnswer}
                    disabled={
                      !userAnswer.trim() ||
                      isSubmittingAnswer ||
                      (interviewQuestions[currentQuestion].videoUrl && !videoEnded)
                    }
                    className={`px-6 py-3 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${
                      isAIAvailable
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                        : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
                    }`}
                  >
                  {isSubmittingAnswer ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {isAIAvailable ? 'AI 평가 중...' : '답변 확인 중...'}
                    </>
                  ) : (
                    <>📝 {isAIAvailable ? '답변 제출' : '답변 확인'}</>
                  )}
                  </button>

                  {isTimerActive && (
                    <button
                      onClick={stopAnswerTimer}
                      className="px-4 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 flex items-center"
                    >
                      ⏹️ 타이머 중지
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* 피드백 로딩 중 표시 */}
            {isSubmittingAnswer && !feedback && (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <div className="flex flex-col items-center space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">AI가 답변을 평가하고 있습니다</h3>
                    <p className="text-gray-600">
                      잠시만 기다려주세요. 곧 상세한 피드백을 제공해드리겠습니다.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 피드백 표시 */}
            {feedback && (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-semibold flex items-center mb-4">
                    {feedback.isOfflineMode ? '📋 답변 가이드' : '🎯 AI 피드백'}
                    {feedback.score !== null && (
                      <div className="ml-4 flex items-center">
                        <span className="text-lg">점수: </span>
                        <span className={`text-2xl font-bold ml-2 ${
                          feedback.score >= 80 ? 'text-green-500' :
                          feedback.score >= 60 ? 'text-yellow-500' : 'text-red-500'
                        }`}>
                          {feedback.score}점
                        </span>
                      </div>
                    )}
                  </h3>
                </div>

{/* AI 모드일 때만 잘한 점, 개선할 점, 상세 피드백 표시 */}
                {!feedback.isOfflineMode && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-green-700 mb-2">✅ 잘한 점</h4>
                          <ul className="space-y-1">
                            {feedback.strengths.map((strength, idx) => (
                              <li key={idx} className="text-sm text-gray-700 flex items-start">
                                <span className="text-green-500 mr-2">•</span>
                                {strength}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-orange-700 mb-2">🔄 개선할 점</h4>
                          <ul className="space-y-1">
                            {feedback.improvements.map((improvement, idx) => (
                              <li key={idx} className="text-sm text-gray-700 flex items-start">
                                <span className="text-orange-500 mr-2">•</span>
                                {improvement}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-6 space-y-6">
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">📋 상세 피드백</h4>
                        <p className="text-gray-700 leading-relaxed">
                          {feedback.detailedFeedback}
                        </p>
                      </div>
                    </div>
                  </>
                )}
                
                <div className={`space-y-6 ${!feedback.isOfflineMode ? 'border-t pt-6' : ''}`}>
                  <div>
                    <h4 className="font-semibold text-blue-700 mb-2">💡 모범 정답 참고</h4>
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                      <p className="text-blue-800 leading-relaxed">
                        {interviewQuestions[currentQuestion].modelAnswer}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      * 모범 정답은 참고용이며, 본인의 경험과 상황에 맞게 답변하는 것이 중요합니다.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-purple-700 mb-2">🎯 예상 키워드</h4>
                    <div className="flex flex-wrap gap-2">
                      {interviewQuestions[currentQuestion].expectedKeywords.map((keyword, index) => (
                        <span key={index} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                          {keyword}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      * 이런 키워드들을 포함하여 답변하면 좋은 평가를 받을 수 있습니다.
                    </p>
                  </div>
                </div>

                <div className="flex justify-center mt-8">
                  <button
                    onClick={nextQuestion}
                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full hover:from-purple-700 hover:to-pink-700 transition-all duration-300"
                  >
                    {currentQuestion < interviewQuestions.length - 1 ? '다음 질문 →' : '면접 완료 🎉'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 리뷰 모드 */}
        {interviewMode === 'review' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎉</span>
              </div>
              <h2 className="text-3xl font-bold mb-4">면접 연습 완료!</h2>
              <p className="text-gray-600">
                {interviewQuestions.length}개 질문을 모두 완료했습니다.<br/>
                실제 면접에서 좋은 결과가 있기를 응원합니다!
              </p>
            </div>
            
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => {
                  setInterviewMode('preparation');
                  setInterviewQuestions([]);
                  setCurrentQuestion(0);
                  setUserAnswer('');
                  setFeedback(null);
                  setIsAIAvailable(true);
                  setQuestionCount(5);
                  setMaxQuestions(0);
                  stopAnswerTimer();
                  setVideoEnded(false);
                  setIsVideoPlaying(false);
                }}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700"
              >
                🔄 다시 연습하기
              </button>
              
              <button
                onClick={() => window.location.href = '/'}
                className="px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700"
              >
                🏠 홈으로 가기
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Interview;